/**
 * useScrollToSelected — scroll a container to its selected item when `selectedId` changes.
 *
 * Contract:
 *   - Items in `containerRef` must carry a `data-item-id="<id>"` attribute.
 *   - Scroll fires when `selectedId` changes to a value it hasn't successfully
 *     scrolled to yet, AND retries whenever any value in `extraDeps` changes
 *     (e.g. list length / data-loaded flags) *while the current `selectedId`
 *     still hasn't been found*.
 *   - Uses `scrollIntoView({ block: 'nearest' })` so the list doesn't jump when
 *     the item is already fully visible.
 *
 * Data-race fix: a deep-linked `selectedId` is often set before the list data
 * that would render its row has finished loading (e.g. paginated/infinite
 * queries, or a slower-resolving store). Without `extraDeps`, the effect only
 * re-fires when `selectedId` itself changes — if the row for that id isn't in
 * the DOM yet, the query for it will never match and the scroll silently
 * no-ops forever. Passing something that changes once the data arrives (e.g.
 * the rendered item count) lets the effect retry once the row exists.
 *
 * SHA-56 pagination-jump fix: once `selectedId` has been found and scrolled to,
 * a *later* `extraDeps` change (e.g. infinite-scroll appending another page —
 * `sortedRuns.length` grows) must NOT re-fire the scroll. Otherwise every page
 * load yanks the viewport back to the already-selected row, which reads as the
 * list "jumping to the top" while the user is scrolling further down. A ref
 * tracks the last `selectedId` that was actually found in the DOM; the effect
 * only scrolls again when `selectedId` changes to something new, or when the
 * current `selectedId` still hasn't been found (so retries-on-data-arrival
 * keep working).
 */

import { useEffect, useRef } from 'react';

export function useScrollToSelected(
  selectedId: string | null | undefined,
  containerRef: { readonly current: HTMLElement | null },
  extraDeps: readonly unknown[] = []
): void {
  const lastFoundIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!selectedId || !containerRef.current) return;

    // Already scrolled to this exact selection — pagination/data growth
    // (extraDeps changing) must not re-trigger the scroll.
    if (lastFoundIdRef.current === selectedId) return;

    const el = containerRef.current.querySelector<HTMLElement>(
      `[data-item-id="${CSS.escape(selectedId)}"]`
    );
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      lastFoundIdRef.current = selectedId;
    }
    // containerRef is a stable ref object — its identity never changes, intentionally omitted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, ...extraDeps]);
}
